import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import Cookies from 'js-cookie';
import { 
  FileText, 
  Sparkles, 
  CheckCircle, 
  Loader2,
  Clock,
  ArrowRight,
  AlertCircle,
  Calendar,
  Snowflake,
  Sun,
  Leaf,
  Flower2
} from 'lucide-react';
import { getApiUrl } from '@/config/api';
import { useNavigate } from 'react-router-dom';
import Logo from '@/components/Logo';

interface ProcessingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  duration: number; // in seconds
  status: 'pending' | 'processing' | 'completed';
}

const SeasonalMenuProcessing = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<{
    progress: number;
    current_step: string;
    status: string;
    error_message?: string;
    menu_id?: string;
  } | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const steps: ProcessingStep[] = [
    {
      id: 'upload',
      title: 'Receiving Your Seasonal Menu',
      description: 'Securely processing your special menu',
      icon: FileText,
      duration: 2,
      status: 'pending'
    },
    {
      id: 'reading',
      title: 'Extracting Seasonal Items',
      description: 'Identifying your seasonal offerings and specials',
      icon: Sparkles,
      duration: 4,
      status: 'pending'
    },
    {
      id: 'organizing',
      title: 'Categorizing Dishes',
      description: 'Organizing items by appetizers, mains, and desserts',
      icon: Sparkles,
      duration: 6,
      status: 'pending'
    },
    {
      id: 'analyzing',
      title: 'Processing Prices',
      description: 'Extracting pricing for seasonal items',
      icon: Sparkles,
      duration: 4,
      status: 'pending'
    },
    {
      id: 'finalizing',
      title: 'Finalizing Seasonal Menu',
      description: 'Preparing your menu for review',
      icon: CheckCircle,
      duration: 2,
      status: 'pending'
    }
  ];

  // Update step statuses
  const getStepStatuses = () => {
    return steps.map((step, index) => {
      // If processing is complete, all steps should be completed
      if (processingComplete) {
        return { ...step, status: 'completed' as const };
      }
      // Otherwise, use the current step logic
      if (index < currentStep) return { ...step, status: 'completed' as const };
      if (index === currentStep) return { ...step, status: 'processing' as const };
      return { ...step, status: 'pending' as const };
    });
  };

  // Poll processing status
  const checkProcessingStatus = useCallback(async () => {
    const processingId = sessionStorage.getItem('seasonal_processing_id');
    if (!processingId) {
      setError('No processing ID found');
      return;
    }

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        console.error('No auth token for processing status');
        navigate('/restaurant-login');
        return;
      }
      // Use seasonal menu processing status endpoint
      const response = await fetch(getApiUrl(`seasonal-menus/processing-status/${processingId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check processing status');
      }

      const data = await response.json();
      setProcessingStatus(data);

      // Map backend status to UI steps
      const stepMapping: { [key: string]: number } = {
        'uploading': 0,
        'Uploaded - Ready to process': 0,
        'extracting': 1,
        'Extracting menu text with AI (100% accuracy)': 1,
        'categorizing': 2,
        'Categorizing menu into sections': 2,
        'analyzing': 3,
        'Analyzing dietary information and categorizing items': 3,
        'finalizing': 4,
        'Finalizing seasonal menu': 4,
        'Caching menu data': 4,
        'completed': 5
      };

      // Try to match the current_step or use status
      let stepIndex = stepMapping[data.current_step];
      if (stepIndex === undefined && data.status === 'completed') {
        stepIndex = 5;
      }
      if (stepIndex === undefined) {
        stepIndex = currentStep;
      }
      
      setCurrentStep(Math.min(stepIndex, steps.length - 1));

      // Check if processing is complete
      if (data.status === 'completed' && data.menu_id) {
        setProcessingComplete(true);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      } else if (data.status === 'failed') {
        setError(data.error_message || 'Processing failed');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      }
    } catch (error) {
      console.error('Error checking processing status:', error);
      setError('Failed to check processing status');
    }
  }, [currentStep, steps.length]);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Polling effect
  useEffect(() => {
    // Start polling immediately
    checkProcessingStatus();
    
    // Set up polling interval
    pollIntervalRef.current = setInterval(checkProcessingStatus, 2000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [checkProcessingStatus]);

  const handleContinue = () => {
    const seasonalMenuId = sessionStorage.getItem('seasonal_menu_id');
    if (seasonalMenuId) {
      navigate(`/seasonal-menu/${seasonalMenuId}/items`);
    } else {
      navigate('/restaurant-dashboard');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSeasonIcon = () => {
    const month = new Date().getMonth();
    if ([11, 0, 1].includes(month)) return <Snowflake className="h-6 w-6" />;
    if ([2, 3, 4].includes(month)) return <Flower2 className="h-6 w-6" />;
    if ([5, 6, 7].includes(month)) return <Sun className="h-6 w-6" />;
    if ([8, 9, 10].includes(month)) return <Leaf className="h-6 w-6" />;
    return <Calendar className="h-6 w-6" />;
  };

  const updatedSteps = getStepStatuses();
  // Use progress from backend if available, otherwise calculate
  const progress = processingStatus?.progress || (processingComplete ? 100 : Math.min((currentStep / steps.length) * 100, 95));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-400/5 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="p-6">
          <Logo className="text-white h-10" />
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center p-3 bg-yellow-400/20 rounded-full mb-6">
              {getSeasonIcon()}
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              {processingComplete ? 'Your Seasonal Menu is Ready!' : 'Processing Your Seasonal Menu'}
            </h1>
            <p className="text-xl text-white/80">
              {processingComplete 
                ? 'All items have been extracted and categorized'
                : 'Our AI is analyzing your seasonal offerings'
              }
            </p>
          </motion.div>

          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="bg-white/10 rounded-full h-4 overflow-hidden backdrop-blur-sm">
              <motion.div
                className="h-full bg-gradient-to-r from-yellow-400 to-yellow-300"
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
            <div className="flex justify-between mt-2 text-white/60 text-sm">
              <span>{progress}% Complete</span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatTime(elapsedTime)}
              </span>
            </div>
          </motion.div>

          {/* Processing Steps */}
          <div className="space-y-4 mb-12">
            {updatedSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`
                  p-6 rounded-xl backdrop-blur-sm border transition-all duration-300
                  ${step.status === 'completed' 
                    ? 'bg-green-500/10 border-green-400/30' 
                    : step.status === 'processing'
                    ? 'bg-yellow-400/10 border-yellow-400/30 shadow-lg shadow-yellow-400/20'
                    : 'bg-white/5 border-white/10'
                  }
                `}
              >
                <div className="flex items-center gap-4">
                  <div className={`
                    p-3 rounded-lg transition-all duration-300
                    ${step.status === 'completed'
                      ? 'bg-green-500/20'
                      : step.status === 'processing'
                      ? 'bg-yellow-400/20 animate-pulse'
                      : 'bg-white/10'
                    }
                  `}>
                    {step.status === 'completed' ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : step.status === 'processing' ? (
                      <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
                    ) : (
                      <step.icon className="w-6 h-6 text-white/40" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`
                      font-semibold text-lg transition-all duration-300
                      ${step.status === 'completed'
                        ? 'text-green-400'
                        : step.status === 'processing'
                        ? 'text-yellow-400'
                        : 'text-white/60'
                      }
                    `}>
                      {step.title}
                    </h3>
                    <p className={`
                      text-sm transition-all duration-300
                      ${step.status === 'completed'
                        ? 'text-green-300/70'
                        : step.status === 'processing'
                        ? 'text-yellow-300/70'
                        : 'text-white/40'
                      }
                    `}>
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Error State */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-6 bg-red-500/10 border border-red-500/30 rounded-xl"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-400 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-red-400 mb-1">Processing Error</h3>
                  <p className="text-red-300/80">{error}</p>
                  <Button
                    onClick={() => navigate('/restaurant-dashboard')}
                    className="mt-4 bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30"
                  >
                    Return to Dashboard
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Success Actions */}
          {processingComplete && !error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <div className="p-6 bg-green-500/10 border border-green-400/30 rounded-xl mb-6">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-green-300 text-lg">
                  Successfully extracted {processingStatus?.total_items_inserted || processingStatus?.item_count || 0} items from your seasonal menu!
                </p>
              </div>
              <Button
                onClick={handleContinue}
                className="bg-yellow-400 text-gray-900 hover:bg-yellow-300 px-8 py-6 text-lg font-semibold rounded-xl inline-flex items-center gap-2"
              >
                Review Seasonal Menu Items
                <ArrowRight className="w-5 h-5" />
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeasonalMenuProcessing;